// ============================================================================
// GtoChart - the preflop hand grid, reusable anywhere in Training
// ----------------------------------------------------------------------------
// Used full-size on the Preflop charts page and inside the slide-up drawer
// the floating Charts button opens on every training screen. Accepts a
// starting situation, seat and stack so the caller can land the player on
// the chart that matches the spot they are in.
// ============================================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, HStack, VStack, Text, Button, useBreakpointValue } from '@chakra-ui/react';
import {
  Action, TABLE_SIZES, TableSize, OPEN_POSITIONS, VS_OPEN_SPOTS, PUSH_STACKS, PUSH_POSITIONS,
  POSITION_NAMES, openChart, vsOpenChart, pushFoldChart, paintChart, handAt,
} from '../../data/gtoRanges';

export type GtoSituation = 'open' | 'vs' | 'push';

export interface GtoChartProps {
  situation?: GtoSituation;
  position?: string;      // UTG, HJ, CO, BTN, SB, BB ...
  stackBB?: number | null; // picks the push/fold stack bucket
  tableSize?: TableSize;
  compact?: boolean;
  /** Hand code to ring on the grid and explain first (the cards the player holds). */
  hand?: string | null;
  /** For situation 'vs': the facing-a-raise spot to open on. */
  spot?: string;
  onPracticeClick?: () => void;
}

const describeHand = (code: string) =>
  code.length === 2 ? `Pocket ${code[0] === 'T' ? 'Ten' : code[0]}s` : `${code[0] === 'T' ? 'Ten' : code[0]}-${code[1] === 'T' ? 'Ten' : code[1]} ${code[2] === 's' ? 'suited' : 'offsuit'}`;

const ACTION_STYLE: Record<Action, { bg: string; label: string; text: string }> = {
  raise: { bg: '#16a34a', label: 'Raise', text: 'white' },
  call: { bg: '#2563eb', label: 'Call', text: 'white' },
  allin: { bg: '#ea580c', label: 'All-in', text: 'white' },
  fold: { bg: '#1f2a37', label: 'Fold', text: '#64748b' },
};

const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode; small?: boolean }> = ({ active, onClick, children, small }) => (
  <Button size="sm" minH={small ? '38px' : '44px'} px={small ? 3 : 4} borderRadius="full" onClick={onClick} flexShrink={0}
    variant={active ? 'solid' : 'outline'} colorScheme={active ? 'cyan' : 'gray'}
    color={active ? 'black' : 'gray.200'} borderColor={active ? 'transparent' : 'whiteAlpha.300'} fontWeight="700" fontSize={small ? 'sm' : 'md'}>
    {children}
  </Button>
);

/** Nearest push/fold bucket for a stack in big blinds. */
export function stackBucket(stackBB: number | null | undefined): string {
  if (!stackBB || stackBB <= 12) return '10 BB';
  if (stackBB <= 17) return '15 BB';
  return '20 BB';
}

// One grid square. A plain button with inline styles, not a styled Box:
// there are 169 of them and the drawer re-renders on every bot action, so
// each square must cost nothing. Memoized on primitives only.
const Cell = React.memo<{ code: string; action: Action; isMine: boolean; isPicked: boolean; fontSize?: string; onPick: (code: string, action: Action) => void }>(
  ({ code, action, isMine, isPicked, fontSize, onPick }) => {
    const style = ACTION_STYLE[action];
    return (
      <button
        type="button"
        onClick={() => onPick(code, action)}
        style={{
          aspectRatio: '1 / 1',
          width: '100%',
          border: 'none',
          borderRadius: 3,
          padding: 0,
          cursor: 'pointer',
          background: isMine ? '#fde68a' : style.bg,
          color: isMine ? '#1a1206' : style.text,
          fontSize,
          fontWeight: action === 'fold' && !isMine ? 500 : 900,
          opacity: action === 'fold' && !isMine ? 0.85 : 1,
          outline: isMine ? '3px solid #fde68a' : isPicked ? '2px solid #fde68a' : 'none',
          outlineOffset: -1,
          boxShadow: isMine ? '0 0 0 2px #0f1722, 0 0 14px rgba(253,230,138,0.9)' : undefined,
          position: 'relative',
          zIndex: isMine ? 1 : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'inherit',
        }}
      >
        {code}
      </button>
    );
  },
);

const normalizePos = (p?: string): string => {
  const u = (p || 'BTN').toUpperCase();
  if (u === 'DEALER' || u === 'BUTTON') return 'BTN';
  if (u === 'MP1' || u === 'MP2') return 'MP';
  return u;
};

const GtoChart: React.FC<GtoChartProps> = ({ situation: initialSituation, position: initialPosition, stackBB, tableSize, compact, hand, spot: initialSpot, onPracticeClick }) => {
  const [situation, setSituation] = useState<GtoSituation>(initialSituation || 'open');
  const [size, setSize] = useState<TableSize>(tableSize || '6-max');
  const [position, setPosition] = useState(normalizePos(initialPosition));
  const [spot, setSpot] = useState(initialSpot && VS_OPEN_SPOTS.includes(initialSpot) ? initialSpot : VS_OPEN_SPOTS[0]);
  const [stack, setStack] = useState(stackBucket(stackBB));
  const [pushPos, setPushPos] = useState(PUSH_POSITIONS.includes(normalizePos(initialPosition)) ? normalizePos(initialPosition) : 'BTN');
  const [picked, setPicked] = useState<{ code: string; action: Action } | null>(null);
  const cellFont = useBreakpointValue({ base: compact ? '8px' : '9px', sm: '11px', md: '13px' });

  // Follow the caller when the spot changes (a new hand dealt while the drawer is open)
  useEffect(() => { if (initialSituation) setSituation(initialSituation); }, [initialSituation]);
  useEffect(() => {
    if (!initialPosition) return;
    const p = normalizePos(initialPosition);
    setPosition(p);
    if (PUSH_POSITIONS.includes(p)) setPushPos(p);
  }, [initialPosition]);
  useEffect(() => { setStack(stackBucket(stackBB)); }, [stackBB]);
  // Follow the action: when a different seat raises, the "someone raised"
  // chart switches to that spot on its own.
  useEffect(() => { if (initialSpot && VS_OPEN_SPOTS.includes(initialSpot)) setSpot(initialSpot); }, [initialSpot]);
  useEffect(() => { if (tableSize) setSize(tableSize); }, [tableSize]);
  useEffect(() => { if (initialSpot && VS_OPEN_SPOTS.includes(initialSpot)) setSpot(initialSpot); }, [initialSpot]);
  // A new hand dealt: explain it straight away instead of waiting for a tap
  useEffect(() => { setPicked(null); }, [hand]);

  const openPositions = OPEN_POSITIONS[size];
  const effectiveOpenPos = openPositions.includes(position) ? position : position === 'BB' ? 'SB' : 'BTN';

  const chart = useMemo(() => {
    if (situation === 'open') return openChart(size, effectiveOpenPos);
    if (situation === 'vs') return vsOpenChart(spot);
    return pushFoldChart(stack, pushPos);
  }, [situation, size, effectiveOpenPos, spot, stack, pushPos]);
  const painted = useMemo(() => paintChart(chart), [chart]);
  const usedActions = (Object.keys(painted.counts) as Action[]).filter(a => a !== 'fold' && painted.counts[a] > 0);
  const label = (a: Action) => chart.layers.find(l => l.action === a)?.label || ACTION_STYLE[a].label;

  // What the grid says about the hand in the player's hand, if we know it
  const handAction = useMemo<Action | null>(() => {
    if (!hand) return null;
    for (let r = 0; r < 13; r++) for (let c = 0; c < 13; c++) if (handAt(r, c) === hand) return painted.grid[r][c];
    return null;
  }, [hand, painted]);
  const shown = picked || (hand && handAction ? { code: hand, action: handAction } : null);
  const shownIsMine = !!shown && shown.code === hand;
  const onPick = useCallback((code: string, action: Action) => setPicked({ code, action }), []);
  const pickedCode = picked?.code;

  // The 169 squares only rebuild when the chart, the ringed hand, the tapped
  // square or the font size changes; everything else on the screen can
  // re-render without touching them.
  const cells = useMemo(() => painted.grid.map((row, r) => row.map((action, c) => {
    const code = handAt(r, c);
    return <Cell key={code} code={code} action={action} isMine={hand === code} isPicked={pickedCode === code} fontSize={cellFont} onPick={onPick} />;
  })), [painted, hand, pickedCode, cellFont, onPick]);

  return (
    <VStack align="stretch" spacing={3}>
      {/* Situation */}
      <HStack spacing={2} flexWrap="wrap" rowGap={2}>
        <Chip small={compact} active={situation === 'open'} onClick={() => setSituation('open')}>Nobody raised yet</Chip>
        <Chip small={compact} active={situation === 'vs'} onClick={() => setSituation('vs')}>Someone raised</Chip>
        <Chip small={compact} active={situation === 'push'} onClick={() => setSituation('push')}>Short stack</Chip>
      </HStack>

      {situation === 'open' && (
        <>
          <HStack spacing={2}>
            {TABLE_SIZES.map(s => <Chip key={s} small active={size === s} onClick={() => setSize(s)}>{s}</Chip>)}
          </HStack>
          <HStack spacing={2} flexWrap="wrap">
            {openPositions.map(p => <Chip key={p} small={compact} active={effectiveOpenPos === p} onClick={() => setPosition(p)}>{p}</Chip>)}
          </HStack>
          <Text color="gray.500" fontSize="sm">{POSITION_NAMES[effectiveOpenPos]}{position === 'BB' && situation === 'open' ? '. In the big blind nobody raised means you check; this shows the small blind.' : ''}</Text>
        </>
      )}
      {situation === 'vs' && (
        <VStack align="stretch" spacing={2}>
          {VS_OPEN_SPOTS.map(s => <Chip key={s} small={compact} active={spot === s} onClick={() => setSpot(s)}>{s}</Chip>)}
        </VStack>
      )}
      {situation === 'push' && (
        <>
          <HStack spacing={2}>{PUSH_STACKS.map(s => <Chip key={s} small={compact} active={stack === s} onClick={() => setStack(s)}>{s}</Chip>)}</HStack>
          <HStack spacing={2}>{PUSH_POSITIONS.map(p => <Chip key={p} small={compact} active={pushPos === p} onClick={() => setPushPos(p)}>{p}</Chip>)}</HStack>
        </>
      )}

      {/* Chart */}
      <Box bg="#0f1722" border="1px solid rgba(148,163,184,0.2)" borderRadius="2xl" p={{ base: 3, md: 4 }}>
        <HStack justify="space-between" align="flex-start" mb={2} flexWrap="wrap" gap={2}>
          <Box>
            <Text color="white" fontWeight="800" fontSize={compact ? 'md' : 'lg'}>{chart.title}</Text>
            <Text color="gray.400" fontSize="sm">{chart.subtitle}</Text>
          </Box>
          <Box textAlign="right">
            <Text color="#22d3ee" fontWeight="800" fontSize="2xl" lineHeight={1}>{painted.percent}%</Text>
            <Text color="gray.500" fontSize="xs">of hands</Text>
          </Box>
        </HStack>
        <HStack spacing={3} mb={2} flexWrap="wrap">
          {[...usedActions, 'fold' as Action].map(a => (
            <HStack key={a} spacing={1.5}>
              <Box w="12px" h="12px" borderRadius="3px" bg={ACTION_STYLE[a].bg} />
              <Text color="gray.200" fontSize="sm">{label(a)}</Text>
            </HStack>
          ))}
        </HStack>
        <Box display="grid" gridTemplateColumns="repeat(13, 1fr)" gap="2px" w="100%">
          {cells}
        </Box>
        <Box mt={3} bg={shownIsMine ? 'rgba(253,230,138,0.12)' : 'whiteAlpha.50'} border={shownIsMine ? '1px solid rgba(253,230,138,0.5)' : undefined} borderRadius="lg" p={3} minH="52px">
          {shown ? (
            <HStack spacing={3} align="flex-start">
              <Box px={3} py={1.5} borderRadius="md" bg={ACTION_STYLE[shown.action].bg} color={ACTION_STYLE[shown.action].text} fontWeight="800" flexShrink={0}>{label(shown.action)}</Box>
              <Box>
                {shownIsMine && <Text color="#fde68a" fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em">Your hand</Text>}
                <Text color="white" fontSize="md">
                  <b>{describeHand(shown.code)}</b>
                  {shown.action === 'fold' ? ' is a fold here.' : shown.action === 'call' ? ' is a call here.' : shown.action === 'allin' ? ': move all-in.' : ' is a raise here.'}
                </Text>
                {picked && hand && picked.code !== hand && (
                  <Button size="xs" variant="link" color="#fde68a" mt={1} onClick={() => setPicked(null)}>Back to my hand</Button>
                )}
              </Box>
            </HStack>
          ) : (
            <Text color="gray.400" fontSize="md">Tap a square to see what to do with that hand.</Text>
          )}
        </Box>
        {chart.note && <Text color="gray.500" fontSize="sm" mt={2}>{chart.note}</Text>}
      </Box>
      {onPracticeClick && (
        <Button minH="48px" colorScheme="purple" fontSize="md" onClick={onPracticeClick}>Practice these spots</Button>
      )}
    </VStack>
  );
};

export default React.memo(GtoChart);
